import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import { useAuth } from '../context/AuthContext';
import { getTeamDisplayName, getLogoPath, isGameLocked } from '../teamUtils';
import '../App.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const parts = dateStr.split('/').map(Number);
  // dd/mm/yyyy
  if (parts.length === 3) {
    const [d, m, y] = parts;
    return `${months[m - 1]} ${d}, ${y}`;
  }
  return dateStr;
};

const resultClass = (result) => {
  if (result === 1) return 'gp-result--win';
  if (result === 0) return 'gp-result--loss';
  return 'gp-result--pending';
};

const resultLabel = (result) => {
  if (result === 1) return 'W';
  if (result === 0) return 'L';
  return '…';
};

// ── Member avatar ─────────────────────────────────────────────────────────────
function MemberAvatar({ name, picture, size = 28 }) {
  return (
    <img
      src={picture || '/default.jpg'}
      alt={name || '?'}
      className="gp-member-avatar"
      style={{ width: size, height: size }}
      onError={e => { e.target.src = '/default.jpg'; }}
    />
  );
}

// ── Distribution bar ─────────────────────────────────────────────────────────
function DistBar({ game }) {
  const { distribution, total_picks, member_count, home_team, away_team } = game;
  const total = total_picks || 0;

  const homePct  = total > 0 ? Math.round((distribution.home / total) * 100) : 0;
  const awayPct  = total > 0 ? Math.round((distribution.away / total) * 100) : 0;
  const drawPct  = total > 0 ? Math.round((distribution.draw / total) * 100) : 0;
  const isEPL    = game.sport === 'EPL';

  const homeDisplay = getTeamDisplayName(home_team);
  const awayDisplay = getTeamDisplayName(away_team);

  return (
    <div className="gp-dist">
      <div className="gp-dist-header">
        <span className="gp-dist-label">
          <i className="fa fa-users" /> {total_picks}/{member_count} picked
        </span>
      </div>
      {total > 0 ? (
        <>
          <div className="gp-dist-bar">
            {homePct > 0 && (
              <div className="gp-dist-seg gp-dist-seg--home" style={{ width: `${homePct}%` }} title={`${homeDisplay}: ${homePct}%`} />
            )}
            {isEPL && drawPct > 0 && (
              <div className="gp-dist-seg gp-dist-seg--draw" style={{ width: `${drawPct}%` }} title={`Draw: ${drawPct}%`} />
            )}
            {awayPct > 0 && (
              <div className="gp-dist-seg gp-dist-seg--away" style={{ width: `${awayPct}%` }} title={`${awayDisplay}: ${awayPct}%`} />
            )}
          </div>
          <div className="gp-dist-legend">
            <span className="gp-dist-pct gp-dist-pct--home">{homeDisplay} {homePct}%</span>
            {isEPL && drawPct > 0 && <span className="gp-dist-pct gp-dist-pct--draw">Draw {drawPct}%</span>}
            <span className="gp-dist-pct gp-dist-pct--away">{awayDisplay} {awayPct}%</span>
          </div>
        </>
      ) : (
        <div className="gp-dist-empty">No votes yet. Be the first!</div>
      )}
    </div>
  );
}

// ── Expandable member vote list ───────────────────────────────────────────────
function MemberVoteList({ game }) {
  const [open, setOpen] = useState(false);
  const { member_picks, not_picked, home_team, away_team } = game;

  const homeDisplay = getTeamDisplayName(home_team);
  const awayDisplay = getTeamDisplayName(away_team);

  const getPickLabel = (team) => {
    if (!team) return '—';
    if (team.toLowerCase() === 'draw') return 'Draw';
    if (team.toLowerCase() === home_team.toLowerCase()) return homeDisplay;
    if (team.toLowerCase() === away_team.toLowerCase()) return awayDisplay;
    return getTeamDisplayName(team);
  };

  const total = (member_picks?.length || 0) + (not_picked?.length || 0);
  if (total === 0) return null;

  return (
    <div className="gp-vote-list">
      <button className="gp-vote-toggle" onClick={() => setOpen(o => !o)}>
        <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} />
        {open ? 'Hide' : 'Show'} member picks
      </button>
      {open && (
        <div className="gp-vote-members">
          {member_picks?.map(m => (
            <Link key={m.user_id} to={`/user/${m.username}`} className="gp-vote-row">
              <MemberAvatar name={`${m.firstName} ${m.lastName}`} picture={m.profile_picture} size={26} />
              <span className="gp-vote-name">@{m.username}</span>
              <span className={`gp-vote-pick${m.result === 1 ? ' gp-vote-pick--win' : m.result === 0 ? ' gp-vote-pick--loss' : ''}`}>
                {getPickLabel(m.picked_team)}
                {m.result === 1 && <i className="fa fa-check gp-vote-icon--win" />}
                {m.result === 0 && <i className="fa fa-times gp-vote-icon--loss" />}
              </span>
            </Link>
          ))}
          {not_picked?.map(m => (
            <Link key={m.user_id} to={`/user/${m.username}`} className="gp-vote-row gp-vote-row--no-pick">
              <MemberAvatar name={`${m.firstName} ${m.lastName}`} picture={m.profile_picture} size={26} />
              <span className="gp-vote-name">@{m.username}</span>
              <span className="gp-vote-pick gp-vote-pick--none">No pick</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Unpicked game card ────────────────────────────────────────────────────────
function UnpickedCard({ game, pendingPick, onPick, isMember }) {
  const navigate = useNavigate();
  const isEPL    = game.sport === 'EPL';
  const home     = game.home_team;
  const away     = game.away_team;
  const homeDisp = getTeamDisplayName(home);
  const awayDisp = getTeamDisplayName(away);
  const locked   = isGameLocked(game);

  const cur = pendingPick;

  const handlePick = (team) => {
    if (!isMember || locked) return;
    onPick(game.game_id, cur === team ? null : team);
  };

  return (
    <div className={`gp-card gp-card--unpicked${locked ? ' gp-card--locked' : ''}`}>
      {/* Sport + date */}
      <div className="gp-card-meta">
        <span className="gp-sport-pill">{game.sport}</span>
        <span className="gp-card-date">{formatDate(game.date)} · {game.time}</span>
        {locked && (
          <span className="gp-lock-badge"><i className="fa fa-lock" /> In Progress</span>
        )}
        <span className="gp-card-link" onClick={() => navigate(`/game/${game.game_id}`)}>
          <i className="fa fa-external-link" />
        </span>
      </div>

      {/* Pick row */}
      <div className="gp-pick-row">
        <div className="gp-team gp-team--home">
          <img
            src={getLogoPath(homeDisp)}
            alt={homeDisp}
            className="gp-team-logo"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <button
            className={`gp-pick-btn${cur === home ? ' gp-pick-btn--selected' : ''}${!isMember || locked ? ' gp-pick-btn--disabled' : ''}`}
            onClick={() => handlePick(home)}
            disabled={!isMember || locked}
          >
            {homeDisp}
          </button>
        </div>

        <div className="gp-vs">
          VS
          {isEPL && (
            <button
              className={`gp-draw-btn${cur === 'Draw' ? ' gp-pick-btn--selected' : ''}${!isMember || locked ? ' gp-pick-btn--disabled' : ''}`}
              onClick={() => handlePick('Draw')}
              disabled={!isMember || locked}
            >
              DRAW
            </button>
          )}
        </div>

        <div className="gp-team gp-team--away">
          <button
            className={`gp-pick-btn${cur === away ? ' gp-pick-btn--selected' : ''}${!isMember || locked ? ' gp-pick-btn--disabled' : ''}`}
            onClick={() => handlePick(away)}
            disabled={!isMember || locked}
          >
            {awayDisp}
          </button>
          <img
            src={getLogoPath(awayDisp)}
            alt={awayDisp}
            className="gp-team-logo"
            onError={e => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>

      {/* Distribution + member list */}
      <DistBar game={game} />
      <MemberVoteList game={game} />
    </div>
  );
}

// ── Picked game card ──────────────────────────────────────────────────────────
function PickedCard({ game }) {
  const navigate   = useNavigate();
  const homeDisp   = getTeamDisplayName(game.home_team);
  const awayDisp   = getTeamDisplayName(game.away_team);
  const myPickDisp = game.my_pick
    ? getTeamDisplayName(game.my_pick)
    : '—';
  const finished = !!game.winning_team;

  return (
    <div className={`gp-card gp-card--picked${finished ? ' gp-card--finished' : ''}`}>
      <div className="gp-card-meta">
        <span className="gp-sport-pill">{game.sport}</span>
        <span className="gp-card-date">{formatDate(game.date)} · {game.time}</span>
        {finished && game.score && <span className="gp-score-pill">{game.score}</span>}
        <span className="gp-card-link" onClick={() => navigate(`/game/${game.game_id}`)}>
          <i className="fa fa-external-link" />
        </span>
      </div>

      <div className="gp-pick-row gp-pick-row--picked">
        <div className="gp-team gp-team--home">
          <img
            src={getLogoPath(homeDisp)}
            alt={homeDisp}
            className="gp-team-logo"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <span className={`gp-team-name${game.my_pick?.toLowerCase() === game.home_team?.toLowerCase() ? ' gp-team-name--mypick' : ''}`}>
            {homeDisp}
          </span>
        </div>
        <div className="gp-vs">VS</div>
        <div className="gp-team gp-team--away">
          <span className={`gp-team-name${game.my_pick?.toLowerCase() === game.away_team?.toLowerCase() ? ' gp-team-name--mypick' : ''}`}>
            {awayDisp}
          </span>
          <img
            src={getLogoPath(awayDisp)}
            alt={awayDisp}
            className="gp-team-logo"
            onError={e => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>

      <div className="gp-my-pick-row">
        <span className="gp-my-pick-label">Your pick:</span>
        <span className="gp-my-pick-team">{myPickDisp}</span>
        <span className={`gp-result-badge ${resultClass(game.my_result)}`}>
          {resultLabel(game.my_result)}
        </span>
      </div>

      <DistBar game={game} />
      <MemberVoteList game={game} />
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function GroupPicksPage() {
  const { groupId } = useParams();
  const { authData } = useAuth();
  const navigate    = useNavigate();
  const token       = authData?.token;

  const [data,        setData]       = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState('');
  const [sport,       setSport]      = useState('');
  const [pendingPicks, setPendingPicks] = useState({});  // gameId → team
  const [submitting,  setSubmitting] = useState(false);
  const [feedback,    setFeedback]   = useState(null);
  const feedbackTimer = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async (sportOverride) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      const s = sportOverride !== undefined ? sportOverride : sport;
      if (s) params.sport = s;
      const res = await axios.get(`${API_BASE}/groups/${groupId}/picks-page`, { headers, params });
      setData(res.data);
      // Default sport tab to first available
      if (!s && res.data.available_sports?.length > 0 && !res.data.group.sport_filter) {
        const firstSport = res.data.available_sports[0];
        setSport(firstSport);
        // re-fetch with sport to get filtered games
        const res2 = await axios.get(`${API_BASE}/groups/${groupId}/picks-page`, {
          headers,
          params: { sport: firstSport },
        });
        setData(res2.data);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load group picks. Please try again.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, token, sport]);

  useEffect(() => {
    fetchData();
    return () => clearTimeout(feedbackTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const handleSportChange = (s) => {
    setSport(s);
    setPendingPicks({});
    fetchData(s);
  };

  const handlePick = (gameId, team) => {
    setPendingPicks(prev => {
      const next = { ...prev };
      if (team === null) delete next[gameId];
      else next[gameId] = team;
      return next;
    });
  };

  const showFeedback = (type, msg) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, msg });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 3500);
  };

  const handleSubmit = async () => {
    const unpickedMap = Object.fromEntries((data?.unpicked || []).map(g => [g.game_id, g]));
    // Filter out any picks for games that have since locked (started)
    const entries = Object.entries(pendingPicks)
      .filter(([gameId]) => {
        const game = unpickedMap[gameId];
        return game && !isGameLocked(game);
      });
    if (entries.length === 0) return;

    const postData = entries.map(([gameId, pickedTeam]) => {
      const game = unpickedMap[gameId];
      return { game_id: gameId, sport: game?.sport || sport, picked_team: pickedTeam };
    });

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/user/make-pick`, postData, { headers });
      setPendingPicks({});
      showFeedback('success', `${entries.length} pick${entries.length !== 1 ? 's' : ''} submitted!`);
      fetchData(sport);
    } catch (e) {
      showFeedback('error', e.response?.data?.error || 'Failed to submit picks. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────
  const isMember     = !!authData?.user;   // picks require JWT; role check is backend-enforced
  const group        = data?.group;
  const isCurated    = data?.is_curated;
  const sports       = data?.available_sports || [];
  const unpicked     = data?.unpicked || [];
  const picked       = data?.picked   || [];
  const pendingCount = Object.keys(pendingPicks).length;

  // Effective sport tabs — if group has a sport filter, no tabs needed
  const showSportTabs = !group?.sport_filter && sports.length > 1;

  return (
    <BaseLayout>
      <div className="gp-wrap">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="gp-header">
          <button className="gp-back-btn" onClick={() => navigate(`/groups/${groupId}`)}>
            <i className="fa fa-arrow-left" />
          </button>
          <div className="gp-header-info">
            <h1 className="gp-title">
              {group ? group.name : 'Group'} — Picks
            </h1>
            <p className="gp-subtitle">
              {isCurated
                ? `${group?.game_count || 0} curated game${group?.game_count !== 1 ? 's' : ''}`
                : 'All games (no curated schedule)'}
              {group?.sport_filter && <> · {group.sport_filter} only</>}
            </p>
          </div>
        </div>

        {/* ── Sport tabs ──────────────────────────────────────────────── */}
        {showSportTabs && !loading && (
          <div className="gp-sport-tabs">
            {sports.map(s => (
              <button
                key={s}
                className={`gp-sport-tab${sport === s ? ' gp-sport-tab--active' : ''}`}
                onClick={() => handleSportChange(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* ── Feedback banner ─────────────────────────────────────────── */}
        {feedback && (
          <div className={`gp-feedback gp-feedback--${feedback.type}`}>{feedback.msg}</div>
        )}

        {loading && <FlameLoader text="Loading picks…" />}
        {error   && <p className="error-text">{error}</p>}

        {!loading && !error && data && (
          <>
            {/* ── Pick Queue ─────────────────────────────────────────── */}
            <section className="gp-section">
              <div className="gp-section-header">
                <h2 className="gp-section-title">
                  <i className="fa fa-fire" /> Pick Queue
                  <span className="gp-count-badge">{unpicked.length}</span>
                </h2>
                {pendingCount > 0 && (
                  <button
                    className="gp-submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting
                      ? <><i className="fa fa-spinner fa-spin" /> Submitting…</>
                      : <><i className="fa fa-check" /> Submit {pendingCount} Pick{pendingCount !== 1 ? 's' : ''}</>
                    }
                  </button>
                )}
              </div>

              {unpicked.length === 0 ? (
                <div className="gp-empty">
                  {picked.length > 0
                    ? <><i className="fa fa-check-circle gp-empty-icon gp-empty-icon--ok" /><p>You've picked all games in this group!</p></>
                    : <><i className="fa fa-calendar-o gp-empty-icon" /><p>No upcoming games in this group's schedule yet.</p></>
                  }
                </div>
              ) : (
                <div className="gp-cards">
                  {unpicked.map(game => (
                    <UnpickedCard
                      key={game.game_id}
                      game={game}
                      pendingPick={pendingPicks[game.game_id] ?? null}
                      onPick={handlePick}
                      isMember={isMember}
                    />
                  ))}
                </div>
              )}

              {pendingCount > 0 && (
                <div className="gp-submit-bar">
                  <button
                    className="gp-submit-btn gp-submit-btn--full"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting
                      ? <><i className="fa fa-spinner fa-spin" /> Submitting…</>
                      : <><i className="fa fa-check" /> Submit {pendingCount} Pick{pendingCount !== 1 ? 's' : ''}</>
                    }
                  </button>
                </div>
              )}
            </section>

            {/* ── Already Picked ────────────────────────────────────── */}
            {picked.length > 0 && (
              <section className="gp-section">
                <div className="gp-section-header">
                  <h2 className="gp-section-title">
                    <i className="fa fa-check-circle" /> Your Picks
                    <span className="gp-count-badge">{picked.length}</span>
                  </h2>
                </div>
                <div className="gp-cards">
                  {picked.map(game => (
                    <PickedCard key={game.game_id} game={game} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </BaseLayout>
  );
}
